import styles from './BarChart.module.css'

/**
 * BarChart — a horizontal labeled bar list, one row per item.
 *
 * Extracted from MetricCard's bar cardtype so it can be reused anywhere a
 * label→value bar list is needed (e.g. the Home tab's `barList` block).
 * Parsing/adapting a source-specific value shape into `items` is left to
 * the caller (see MetricCard's own `parseBars`) — this component only
 * renders an already-normalized array.
 *
 * Props:
 *   items    {Array<{label: string, value: number}>}
 *   unit     {string}  — suffix appended to each printed value. Defaults
 *                        to '%' so existing percent bars are unchanged;
 *                        pass '' for raw counts.
 *   barScale {string}  — width strategy:
 *                          default — value is already 0–100, clamped to that range.
 *                          'max'   — value is a raw magnitude; width is scaled
 *                                    relative to the largest value in the set,
 *                                    and the raw value is printed.
 *   color      {string}  — bar fill color (CSS color value). Defaults to the
 *                          semantic blue MetricCard has always used.
 *   valueColor {string}  — optional CSS color for the trailing printed value
 *                          text (independent of the fill color).
 *   compact    {boolean} — tighter row spacing, used in the Skill Details
 *                          modal's compact metric tiles.
 *   columns    {number}  — lay bars out in this many columns instead of one
 *                          stacked column (e.g. 4 bars at columns=2 take two
 *                          rows instead of four), filling down each column
 *                          before starting the next — so with columns=2, items
 *                          0 and 1 stack in the left column and 2 and 3 stack
 *                          in the right, rather than alternating left/right.
 *                          Defaults to 1.
 *   tight      {boolean} — optional; columns > 1 only. Shrinks the grid's row-gap/
 *                          bar-gap/track and bumps the label/value font size, with
 *                          single-line label truncation — for a multi-column chart
 *                          that has to match shorter non-bar siblings in the same
 *                          card row (the Cases page's Top Issues card). Leaves the
 *                          plain .grid layout (e.g. Team page's Skill Levels tile)
 *                          untouched. Defaults to false.
 */
export default function BarChart({ items = [], unit = '%', barScale, color = 'var(--color-semantic-blue)', valueColor, compact = false, columns = 1, tight = false }) {
  if (!items.length) return null
  const max = barScale === 'max' ? Math.max(...items.map(b => b.value), 0) || 1 : 100
  const rows = Math.ceil(items.length / columns)
  return (
    <div
      className={`${styles.barChart} ${compact ? styles.compact : ''} ${columns > 1 ? styles.grid : ''} ${tight ? styles.tight : ''}`}
      style={columns > 1 ? {
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gridTemplateRows: `repeat(${rows}, auto)`,
        gridAutoFlow: 'column',
      } : undefined}
    >
      {items.map(b => (
        <div key={b.label} className={styles.bar}>
          <div className={styles.barHeader}>
            <span className={styles.barLabel} title={b.label}>{b.label}</span>
            <span className={styles.barValue} style={valueColor ? { color: valueColor } : undefined}>{b.value}{unit}</span>
          </div>
          <span className={styles.barTrack}>
            <span
              className={styles.barFill}
              style={{
                width: `${Math.max(0, Math.min(100, (b.value / max) * 100))}%`,
                backgroundColor: color,
              }}
            />
          </span>
        </div>
      ))}
    </div>
  )
}
